import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { CTASection } from "@/components/CTASection";
import { PhotoFrame } from "@/components/TeamCards";
import { Reveal, EASE } from "@/components/Reveal";
import { TEAM, MOTTO, IMG } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";

const BioSection = ({ section }) => (
  <div className="mt-8">
    <h4 className="font-oswald uppercase tracking-tight text-xl lg:text-2xl text-dst-red mb-3">{section.title}</h4>
    {section.intro?.map((p, i) => (
      <p key={i} className="text-white/65 leading-relaxed mb-3">{p}</p>
    ))}
    {section.list && (
      <ul className="my-4 space-y-2">
        {section.list.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-white/85">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-dst-red rounded-full" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
    {section.outro?.map((p, i) => (
      <p key={i} className="text-white/65 leading-relaxed mb-3">{p}</p>
    ))}
  </div>
);

export default function Team() {
  useSeo({
    title: "Meet The Team | DST New Braunfels Coaches & Directors",
    description:
      "Meet the DST New Braunfels coaching staff — Mary Stephens (Softball Director), Dennis 'DK' Koenck (Strength Coach), Johnny Medina Jr. (Hitting Coach & Baseball Director), and Ronnie Thames (Baseball Director).",
  });

  return (
    <>
      <PageHeader
        eyebrow="Meet The Team"
        title={<>The Coaches<br />Behind DST.</>}
        subtitle="Collegiate and professional experience across softball, baseball, and strength training — all focused on developing competitors."
        image={IMG.facilityGym}
      />

      <section className="bg-dst-ink grain" data-testid="team-bios">
        <div className="container-dst relative z-10 py-8">
          {TEAM.map((m, idx) => (
            <motion.article
              key={m.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE }}
              data-testid={`team-member-${idx}`}
              className={`py-12 lg:py-20 ${idx !== 0 ? "border-t border-white/10" : ""}`}
            >
              {/* Header: photo + name (stacks on mobile, no sticky) */}
              <div className="grid md:grid-cols-[280px_1fr] gap-8 lg:gap-12 items-center mb-10">
                <PhotoFrame member={m} className="w-full max-w-[300px] mx-auto md:mx-0 aspect-[3/4]" />
                <div>
                  <div className="font-oswald text-5xl lg:text-6xl font-700 text-white/10 leading-none">0{idx + 1}</div>
                  <h2 className="mt-2 font-oswald font-700 uppercase tracking-tight text-4xl lg:text-5xl text-white">{m.name}</h2>
                  <p className="text-dst-red font-oswald uppercase tracking-wide mt-1 text-lg">{m.role}</p>
                  <p className="mt-4 font-oswald uppercase tracking-tight text-xl lg:text-2xl text-white/90 leading-snug">
                    {m.tagline}
                  </p>
                </div>
              </div>

              {/* Bio: single readable column (no split-scroll) */}
              <div className="max-w-3xl">
                {m.intro.map((p, i) => (
                  <p key={i} className="text-white/70 leading-relaxed text-lg mb-4">{p}</p>
                ))}

                {m.sections.map((s) => (
                  <BioSection key={s.title} section={s} />
                ))}

                {/* Career highlights */}
                <div className="mt-10 bg-dst-surface border border-white/10 p-6 lg:p-8">
                  <h4 className="font-oswald uppercase tracking-tight text-xl lg:text-2xl mb-5 flex items-center gap-3">
                    <span className="h-5 w-1 bg-dst-red" /> Career Highlights
                  </h4>
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {m.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/75 text-sm leading-relaxed">
                        <Check size={16} className="text-dst-red mt-0.5 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.article>
          ))}

          <Reveal className="border-t border-white/10 py-14 lg:py-20">
            <div className="border-l-2 border-dst-red pl-6 lg:pl-8 max-w-4xl">
              <p className="font-oswald text-xl lg:text-3xl uppercase tracking-tight leading-snug text-white/90">
                "{MOTTO}"
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
